import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="buylist-search"
export default class extends Controller {
  static targets = ["input", "results", "spinner", "gameFilter"]
  static values = { storeSlug: String, searchUrl: String, addToCartUrl: String }

  connect() {
    this.timeout = null
    this.currentGame = ""
  }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout)
  }

  search() {
    if (this.timeout) clearTimeout(this.timeout)

    const query = this.inputTarget.value.trim()

    if (query.length < 2) {
      this.showEmptyState()
      return
    }

    this.timeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  filterGame(event) {
    const game = event.currentTarget.dataset.game || ""
    this.currentGame = game

    // Update active tab styling
    this.gameFilterTargets.forEach(btn => {
      if (btn.dataset.game === game) {
        btn.className = "px-4 py-1.5 text-sm font-semibold rounded-full transition bg-primary-100 text-primary-700"
      } else {
        btn.className = "px-4 py-1.5 text-sm font-semibold rounded-full transition bg-slate-100 text-slate-600 hover:bg-slate-200"
      }
    })

    // Re-search if there's a query
    const query = this.inputTarget.value.trim()
    if (query.length >= 2) {
      this.performSearch(query)
    }
  }

  async performSearch(query) {
    this.showSpinner()

    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.set("q", query)
      if (this.currentGame) url.searchParams.set("game", this.currentGame)

      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Search failed")

      const data = await response.json()
      this.displayResults(data.results || [])
    } catch (error) {
      console.error("Buylist search error:", error)
      this.resultsTarget.innerHTML = `
        <div class="text-center py-8 text-slate-500">
          <p>Something went wrong. Please try again.</p>
        </div>
      `
    } finally {
      this.hideSpinner()
    }
  }

  displayResults(cards) {
    if (cards.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="text-center py-12 text-slate-400">
          <svg class="w-12 h-12 mx-auto mb-3 text-slate-300" fill="none" viewBox="0 0 24 24" stroke-width="1" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <p class="font-medium">No cards found on our buylist</p>
          <p class="text-sm mt-1">Try a different search term</p>
        </div>
      `
      return
    }

    const html = cards.map(card => this.renderCard(card)).join("")
    this.resultsTarget.innerHTML = html
  }

  renderCard(card) {
    const prices = card.prices || {}
    const conditionRows = Object.entries(prices).map(([cond, priceData]) => {
      const price = priceData.buy_price_cents
      if (!price || price <= 0) return ""
      const condDisplay = cond.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())
      return `
        <div class="flex items-center justify-between py-2 px-3 hover:bg-slate-50 rounded-lg group">
          <div class="flex items-center gap-3">
            <span class="text-sm text-slate-700">${this.escapeHtml(condDisplay)}</span>
            <span class="text-sm font-semibold text-primary-600">$${(price / 100).toFixed(2)}</span>
          </div>
          <form action="${this.addToCartUrl()}" method="post" class="flex items-center gap-2">
            <input type="hidden" name="authenticity_token" value="${this.csrfToken()}">
            <input type="hidden" name="card_id" value="${card.id}">
            <input type="hidden" name="condition" value="${cond}">
            <input type="number" name="quantity" value="1" min="1" max="99" class="w-14 text-sm border border-slate-300 rounded-lg px-2 py-1 text-center focus:ring-2 focus:ring-primary-500 opacity-0 group-hover:opacity-100 transition">
            <button type="submit" class="text-xs font-semibold text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-3 py-1 rounded-lg transition opacity-0 group-hover:opacity-100">
              Add
            </button>
          </form>
        </div>
      `
    }).filter(Boolean).join("")

    if (!conditionRows) return ""

    return `
      <div class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div class="px-5 py-4 border-b border-slate-100">
          <h3 class="font-semibold text-slate-900">${this.escapeHtml(card.name)}</h3>
          <p class="text-sm text-slate-500">${this.escapeHtml(card.card_set || "")} ${card.card_number ? `#${this.escapeHtml(card.card_number)}` : ""}</p>
        </div>
        <div class="divide-y divide-slate-50 px-2 py-1">
          ${conditionRows}
        </div>
      </div>
    `
  }

  addToCartUrl() {
    return this.addToCartUrlValue || `/s/${this.storeSlugValue}/buylist/add_to_cart`
  }

  showSpinner() {
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("hidden")
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("hidden")
  }

  showEmptyState() {
    this.resultsTarget.innerHTML = `
      <div class="text-center py-16 text-slate-400">
        <svg class="w-16 h-16 mx-auto mb-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke-width="1" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
        <p class="text-lg font-medium">Search for cards above</p>
        <p class="text-sm mt-1">Type at least 2 characters to see buy prices</p>
      </div>
    `
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : ""
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
